import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import AppfaceLogo from "@/components/AppfaceLogo";

const navItems = [
  { id: "home", label: "Home", path: "/" },
  { 
    id: "services", 
    label: "Services", 
    path: "/services",
    dropdown: [
      { label: "Linux Support", path: "/services#linux" },
      { label: "HPC Support", path: "/services#hpc" },
      { label: "AI Enablement", path: "/services#ai" },
      { label: "Digital Transformation", path: "/services#digital" },
      { label: "App Development", path: "/services#app" },
      { label: "Cloud & DevOps", path: "/services#cloud" },
      { label: "Digital Public Infrastructure", path: "/services#dpi" },
      { label: "Strategic Advisory", path: "/advisory", highlight: true },
    ]
  },
  { 
    id: "solutions", 
    label: "Solutions", 
    path: "/solutions"
  },
  { 
    id: "mandi", 
    label: "Mandi Trades", 
    path: "/mandi-trades"
  },
  { id: "advisory", label: "Advisory", path: "/advisory" },
  { id: "videos", label: "Videos", path: "/videos" },
  { id: "about", label: "About", path: "/about" },
  { id: "contact", label: "Contact", path: "/contact" },
];

const DropdownMenu = ({ item, isOpen, onToggle, onClose }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 text-sm transition-colors ${
          isOpen ? "text-[#F5F5F7]" : "text-[#86868B] hover:text-[#F5F5F7]"
        }`}
        data-testid={`nav-${item.id}`}
      >
        {item.label}
        <ChevronDown 
          size={14} 
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`} 
        />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 mt-2 py-2 min-w-[200px] bg-[#1C1C1E]/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl"
          >
            {item.dropdown.map((subItem) => (
              <Link
                key={subItem.label}
                to={subItem.path}
                onClick={onClose}
                className={`block px-4 py-2 text-sm transition-colors ${
                  subItem.highlight 
                    ? "text-[#2997FF] hover:text-[#F5F5F7] hover:bg-white/10 border-t border-white/10 mt-1 pt-3" 
                    : "text-[#86868B] hover:text-[#F5F5F7] hover:bg-white/10"
                }`}
              >
                {subItem.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const handleDropdownToggle = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "header-glass" : "bg-black/80 backdrop-blur-md"
      }`}
    >
      <nav className="max-w-[1200px] mx-auto px-6 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="logo"
            >
              <AppfaceLogo className="h-24 w-auto" />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                {item.dropdown ? (
                  <DropdownMenu
                    item={item}
                    isOpen={openDropdown === item.id}
                    onToggle={() => handleDropdownToggle(item.id)}
                    onClose={() => setOpenDropdown(null)}
                  />
                ) : (
                  <Link
                    to={item.path}
                    className={`text-sm transition-colors ${
                      location.pathname === item.path
                        ? "text-[#F5F5F7]"
                        : "text-[#86868B] hover:text-[#F5F5F7]"
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    {item.label}
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#F5F5F7]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 glass rounded-2xl overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-4 px-2">
                {navItems.map((item) => (
                  <div key={item.id}>
                    {item.dropdown ? (
                      <div>
                        <button
                          onClick={() => handleDropdownToggle(item.id)}
                          className={`w-full flex items-center justify-between py-3 px-4 rounded-xl transition-all ${
                            location.pathname === item.path
                              ? "text-[#F5F5F7] bg-white/10"
                              : "text-[#86868B] hover:text-[#F5F5F7] hover:bg-white/5"
                          }`}
                        >
                          {item.label}
                          <ChevronDown 
                            size={16} 
                            className={`transition-transform ${openDropdown === item.id ? "rotate-180" : ""}`} 
                          />
                        </button>
                        <AnimatePresence>
                          {openDropdown === item.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4"
                            >
                              <Link
                                to={item.path}
                                className="block py-2 px-4 text-sm text-[#F5F5F7] hover:bg-white/5 rounded-lg"
                                data-testid={`mobile-nav-${item.id}`}
                              >
                                View All
                              </Link>
                              {item.dropdown.map((subItem) => (
                                <Link
                                  key={subItem.label}
                                  to={subItem.path}
                                  className="block py-2 px-4 text-sm text-[#86868B] hover:text-[#F5F5F7] hover:bg-white/5 rounded-lg"
                                >
                                  {subItem.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        className={`block py-3 px-4 rounded-xl transition-all ${
                          location.pathname === item.path
                            ? "text-[#F5F5F7] bg-white/10"
                            : "text-[#86868B] hover:text-[#F5F5F7] hover:bg-white/5"
                        }`}
                        data-testid={`mobile-nav-${item.id}`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
